'use client';

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Input, Chip, Avatar, Tooltip, Button, Link } from '@heroui/react';
import { IdView, Typography, UniversalTable } from '../../../../../components';
import { FilterSelect, FilterOption, ResponsiveTooltip } from '../../../../../components/ui';
import {
  EmptyContentClientIc,
  ResetFiltersIc,
  SearchInputIc,
  CalendarIcon,
} from '../../../../../components/icons';
import clientRevshareService from '../../../../../api/client/clientRevshareService';
import { useTranslations } from '../../../../../contexts/translation.context';

// Cost schema from API - matches backend response
interface CostClient {
  _id: string;
  name?: string;
  avatar?: string;
}

interface CostPartner {
  _id: string;
  name?: string;
  avatar?: string;
}

interface CostModel {
  _id: string;
  name?: string;
  avatar?: string;
  of_user_name?: string;
  of_user_id?: number;
  overall_popularity_percent?: number;
}

interface CostData {
  _id: string;
  amount: string; // Backend returns string
  status: 'in_pending' | 'paid';
  created_at: string;
  description?: string | null;
  company_id: number;
  inner_company_id: string;
  client: CostClient;
  partner: CostPartner;
  model: CostModel;
}

interface InvoicesTabProps {
  modelId?: string; // This is the internal _id of the model
}

// Status icon component with colored dot
const StatusDot = ({ color }: { color: string }) => (
  <div className="size-[0.4375rem] rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
);

const InvoicesTab: React.FC<InvoicesTabProps> = ({ modelId }) => {
  const { t, lang } = useTranslations();

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [invoices, setInvoices] = useState<CostData[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Status filter options
  const statusOptions: FilterOption[] = useMemo(
    () => [
      { key: 'all', label: t('accountPage.invoices.status.all') || 'Все' },
      { key: 'paid', label: t('accountPage.invoices.status.paid') || 'Оплачен' },
      { key: 'in_pending', label: t('accountPage.invoices.status.in_pending') || 'Ожидает оплаты' },
    ],
    [t]
  );

  // Check if there are any active filters
  const hasActiveFilters = useMemo(() => {
    return searchQuery !== '' || statusFilter !== 'all';
  }, [searchQuery, statusFilter]);

  // Reset filters handler
  const handleResetFilters = useCallback(() => {
    setSearchQuery('');
    setDebouncedSearch('');
    setStatusFilter('all');
    setCurrentPage(1);
  }, []);

  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Load invoices from API
  const loadInvoices = useCallback(async () => {
    if (!modelId) return;

    setIsLoading(true);
    try {
      const params: any = {
        page: currentPage,
        page_size: pageSize,
        model_id: modelId,
      };

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await clientRevshareService.getBills(params);

      if (response && response.objects && Array.isArray(response.objects)) {
        setInvoices(response.objects);
        setTotalCount(response.count || response.objects.length);
      } else {
        setInvoices([]);
        setTotalCount(0);
      }
    } catch (error: any) {
      console.error('Error loading invoices:', error);
      setInvoices([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [modelId, currentPage, pageSize, debouncedSearch, statusFilter]);

  // Load data on mount and when params change
  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // Get short ID for display (last 4 chars of company_id or _id)
  const getShortId = (invoice: CostData) => {
    if (invoice.company_id) {
      return invoice.company_id.toString().slice(-5);
    }
    return invoice._id.slice(-4);
  };

  // Copy ID to clipboard
  const handleCopyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Format date to relative time with UTC fix
  const formatRelativeDate = (dateString: string): string => {
    try {
      // Ensure the date is parsed as UTC if no timezone is specified
      const dateStr =
        dateString.endsWith('Z') || dateString.includes('+') || dateString.includes('-', 10)
          ? dateString
          : dateString + 'Z';
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

      if (lang === 'ru') {
        if (diffDays > 0) {
          return `${diffDays}д. ${diffHours}ч. назад`;
        } else if (diffHours > 0) {
          return `${diffHours}ч. назад`;
        } else {
          return 'только что';
        }
      } else {
        if (diffDays > 0) {
          return `${diffDays}d ${diffHours}h ago`;
        } else if (diffHours > 0) {
          return `${diffHours}h ago`;
        } else {
          return 'just now';
        }
      }
    } catch (error) {
      return dateString;
    }
  };

  // Table columns
  const columns = [
    { name: 'ID', key: 'id' },
    { name: t('accountPage.invoices.columns.campaign') || 'Кампания', key: 'campaign' },
    { name: t('accountPage.invoices.columns.amount') || 'Сумма', key: 'amount' },
    { name: t('accountPage.invoices.columns.status') || 'Статус', key: 'status' },
    { name: t('accountPage.invoices.columns.date') || 'Дата', key: 'date' },
  ];

  // Get status config
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'paid':
        return {
          label: t('accountPage.invoices.status.paid') || 'Оплачен',
          color: '#17c964',
        };
      case 'in_pending':
      default:
        return {
          label: t('accountPage.invoices.status.in_pending') || 'Ожидает оплаты',
          color: '#f5a524',
        };
    }
  };

  // Render table cells
  const renderCell = useCallback(
    (invoice: CostData, columnKey: string) => {
      switch (columnKey) {
        case 'id':
          return (
            <IdView textToCopy={invoice._id || invoice.company_id}>{invoice._id.slice(-4)}</IdView>
          );

        case 'campaign':
          const model = invoice.model;
          const popularityPercent = model?.overall_popularity_percent || 0;

          return (
            <div className="flex items-center gap-4 w-[16.25rem]">
              <Avatar
                src={model?.avatar}
                className="w-10 h-10 rounded-3 flex-shrink-0"
                radius="lg"
                showFallback
              />
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Typography variant="label2" className="!text-auth-ink">
                    {invoice.inner_company_id
                      ? `QV${invoice.inner_company_id.slice(-2).toUpperCase()}`
                      : 'N/A'}
                  </Typography>
                  {model?.of_user_name && (
                    <div className="flex items-center gap-0.5">
                      <Link
                        href={`https://onlyfans.com/${model.of_user_name}`}
                        target="_blank"
                        className="text-partner text-sm font-normal leading-5 hover:underline"
                      >
                        @{model.of_user_name}
                      </Link>
                      <ResponsiveTooltip
                        content={invoice.description || t('accountPage.invoices.columns.campaign')}
                        placement="top"
                        classNames={{
                          content: 'max-w-[15rem] break-all',
                        }}
                      >
                        <button className="p-0.5">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z"
                              fill="#A8A8B0"
                            />
                            <path
                              d="M8 11V8M8 5.5V5"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                        </button>
                      </ResponsiveTooltip>
                    </div>
                  )}
                </div>
                <Chip
                  size="sm"
                  classNames={{
                    base: 'bg-[rgba(245,165,36,0.2)] h-6 px-2 rounded-full',
                    content: 'text-warning text-xs font-normal p-0',
                  }}
                >
                  {popularityPercent.toFixed(1)}%
                </Chip>
              </div>
            </div>
          );

        case 'amount':
          const amount = parseFloat(invoice.amount) || 0;
          return (
            <div className="flex flex-col items-start">
              <Typography variant="label2" className="!text-auth-ink !font-semibold">
                ${amount.toFixed(2)}
              </Typography>
            </div>
          );

        case 'status':
          const config = getStatusConfig(invoice.status);
          return (
            <div className="flex items-start">
              <Chip
                size="sm"
                startContent={<StatusDot color={config.color} />}
                classNames={{
                  base: 'bg-auth-surface h-6 px-2 rounded-2 gap-1',
                  content: 'text-auth-ink text-xs font-normal p-0 pl-1',
                }}
              >
                {config.label}
              </Chip>
            </div>
          );

        case 'date':
          return (
            <div className="flex items-center gap-1">
              <CalendarIcon className="w-3.5 h-3.5 text-auth-muted" />
              <Typography variant="body3" className="!text-auth-muted">
                {formatRelativeDate(invoice.created_at)}
              </Typography>
            </div>
          );

        default:
          return null;
      }
    },
    [copiedId, t, lang]
  );

  // Page change handler
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Page size change handler
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  // Pagination for table
  const pagination = {
    count: totalCount,
    page_size: pageSize,
    page: currentPage,
  };

  return (
    <div className="flex flex-col h-full gap-6">
      {/* Title */}
      <Typography variant="h6" className="!text-auth-ink !text-xl !font-medium">
        {t('accountPage.invoices.title') || 'Счета аккаунта'}
      </Typography>

      {/* Filters */}
      <div className="flex items-center gap-4 max-md:h-10 [&>*]:shrink-0 max-md:overflow-x-auto max-md:px-4 max-md:-mx-4 scroller-none">
        {/* Search */}
        <Input
          type="text"
          placeholder={t('accountPage.invoices.search.placeholder') || 'Имя / ID'}
          size="sm"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          endContent={<SearchInputIc />}
          classNames={{
            mainWrapper: 'w-[17.0625rem]',
            base: 'w-[17.0625rem]',
            input: 'text-sm text-auth-ink placeholder:text-neutral-700',
            inputWrapper:
              'rounded-2 h-9 w-[17.0625rem] bg-auth-surface border-none shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]',
            innerWrapper: 'bg-transparent',
          }}
        />

        {/* Status filter */}
        <FilterSelect
          placeholder={t('accountPage.invoices.filters.status') || 'Статус'}
          options={statusOptions}
          selectedKey={statusFilter}
          variant="campaign"
          onSelectionChange={(value) => setStatusFilter(value)}
          showPlaceholderWithValue={true}
          startContent={
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.7233 8.26327L7.27509 4.81074C8.92455 2.57903 9.74929 1.46318 10.5188 1.69898C11.2882 1.93478 11.2882 3.30338 11.2882 6.04057V6.29865C11.2882 7.2859 11.2882 7.77952 11.6037 8.08915L11.6204 8.10519C11.9426 8.40827 12.4564 8.40827 13.4839 8.40827C15.333 8.40827 16.2575 8.40827 16.57 8.96906C16.5751 8.97834 16.5802 8.9877 16.585 8.99713C16.88 9.5666 16.3447 10.2909 15.2741 11.7394L12.7223 15.1919C11.0728 17.4236 10.248 18.5394 9.47856 18.3036C8.7091 18.0678 8.70912 16.6992 8.70916 13.962L8.70916 13.704C8.70917 12.7168 8.70918 12.2231 8.39373 11.9135L8.37704 11.8975C8.05478 11.5944 7.54102 11.5944 6.51349 11.5944C4.66443 11.5944 3.7399 11.5944 3.42745 11.0336C3.42227 11.0243 3.41724 11.0149 3.41236 11.0055C3.1174 10.436 3.6527 9.71178 4.7233 8.26327Z"
                stroke="#A8A8B0"
              />
            </svg>
          }
          autoWidth={true}
        />

        {!hasActiveFilters && <div className="shrink-0 w-8 h-2 md:hidden"></div>}

        {/* Reset filters button */}
        {hasActiveFilters && (
          <ResponsiveTooltip
            content={t('partner.revshare.offers.filters.resetTooltip') || 'Сбросить фильтры'}
            placement="top"
          >
            <Button
              //@ts-ignore
              size="xs"
              className="h-9 rounded-2 !w-9 p-0 bg-auth-surface shrink-0"
              onPress={handleResetFilters}
            >
              <ResetFiltersIc />
            </Button>
          </ResponsiveTooltip>
        )}
      </div>

      {/* Invoices Table */}
      <div className="w-full h-full md:pb-[4.125rem]">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center gap-4">
              <div className="text-auth-muted text-base">
                {t('accountPage.invoices.loading') || 'Загрузка...'}
              </div>
            </div>
          </div>
        ) : (
          <UniversalTable
            data={invoices}
            columns={columns}
            renderCell={renderCell}
            pagination={pagination}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            currentPage={currentPage}
            currentPageSize={pageSize}
            emptyContent={
              <div className="text-center py-12">
                <div className="flex flex-col items-center gap-4">
                  <EmptyContentClientIc />
                  <div className="flex flex-col gap-1.5">
                    <Typography variant="h6" className="!text-xl !font-semibold !leading-7">
                      {t('accountPage.invoices.empty.title') || 'Нет счетов'}
                    </Typography>
                    <Typography
                      className="!text-neutral-500 !text-base !font-medium !leading-6"
                      variant="body1"
                    >
                      {t('accountPage.invoices.empty.description') ||
                        'Счета появятся после создания кампаний'}
                    </Typography>
                  </div>
                </div>
              </div>
            }
            isSearchEmpty={searchQuery.length > 0 && invoices.length === 0}
          />
        )}
      </div>
    </div>
  );
};

export default InvoicesTab;
